"use client";

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { FunctionSquare, Variable } from "lucide-react";
import type { SilaeVariableType } from "@/types/silae";
import { VariableSelector } from "./VariableSelector";
import { useSilaeVariables } from "@/hooks/useSilaeVariables";
import type { SilaeVariable } from "@/types/silae";
import {
  syntaxColors,
  getSyntaxPatterns,
  getCurrentWord,
  getFilteredCompletions,
  applySyntaxHighlighting,
  handleBracketInsertion,
  type CompletionItem,
} from "../../utils/silae-editor-utils";
import type { FormikProps } from "formik";
import { getIn } from "formik";
import type { SilaeFormData } from "@/types/silae";

interface SilaeExpressionEditorPropsBase {
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  height?: string;
  error?: string;
}

// Interface pour utilisation avec value/onChange (mode classique)
interface SilaeExpressionEditorPropsWithValue extends SilaeExpressionEditorPropsBase {
  value: string;
  onChange: (value: string) => void;
  formik?: never;
  fieldPrefix?: never;
}

// Interface pour utilisation avec Formik (nouvelle structure hiérarchique)
interface SilaeExpressionEditorPropsWithFormik extends SilaeExpressionEditorPropsBase {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix: string;
  value?: never;
  onChange?: never;
}

type SilaeExpressionEditorProps =
  | SilaeExpressionEditorPropsWithValue
  | SilaeExpressionEditorPropsWithFormik;

/**
 * Éditeur d'expression SILAE avec coloration syntaxique et auto-complétion
 * Spécialement conçu pour les expressions complexes
 *
 * Supporte deux modes d'utilisation :
 * 1. Mode classique avec value/onChange (compatible avec l'ancien code)
 * 2. Mode Formik avec fieldPrefix (pour la nouvelle structure hiérarchique)
 *
 * @example
 * // Mode classique
 * <SilaeExpressionEditor
 *   value={formula}
 *   onChange={setFormula}
 * />
 *
 * @example
 * // Mode Formik (nouvelle structure)
 * <SilaeExpressionEditor
 *   formik={formik}
 *   fieldPrefix="ifBranch.bonuses.0.bases.0.manualFormula"
 * />
 */
export const SilaeExpressionEditor: React.FC<SilaeExpressionEditorProps> = (
  props,
) => {
  const {
    placeholder,
    className,
    readOnly = false,
    height = "200px",
    error: errorProp,
  } = props;

  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const [highlightedContent, setHighlightedContent] = useState<
    React.ReactElement[]
  >([]);
  const [showCompletions, setShowCompletions] = useState(false);
  const [completions, setCompletions] = useState<CompletionItem[]>([]);
  const [selectedCompletionIndex, setSelectedCompletionIndex] = useState(0);
  const [completionPosition, setCompletionPosition] = useState({
    top: 8,
    left: 8,
  });
  const [variableSelectorOpen, setVariableSelectorOpen] = useState(false);
  const [variableSearchTerm, setVariableSearchTerm] = useState("");
  const [selectedVariable, setSelectedVariable] =
    useState<SilaeVariable | null>(null);

  // Hook pour les variables Silae depuis Redux
  const {
    variables: silaeVariables,
    frequentlyUsedVariables: getFrequentlyUsedVariablesData,
  } = useSilaeVariables();

  // Détecter le mode d'utilisation
  const isFormikMode = "formik" in props && "fieldPrefix" in props;

  // Récupérer la valeur selon le mode
  const value = useMemo(() => {
    if (isFormikMode && props.formik && props.fieldPrefix) {
      return (getIn(props.formik.values, props.fieldPrefix) as string) || "";
    }
    return props.value || "";
  }, [isFormikMode, props]);

  // Récupérer l'erreur selon le mode
  const error = useMemo(() => {
    if (errorProp) return errorProp;
    if (isFormikMode && props.formik && props.fieldPrefix) {
      const fieldError = getIn(props.formik.errors, props.fieldPrefix);
      const fieldTouched = getIn(props.formik.touched, props.fieldPrefix);
      return fieldTouched && fieldError ? String(fieldError) : undefined;
    }
    return undefined;
  }, [errorProp, isFormikMode, props]);

  // Fonction pour mettre à jour la valeur selon le mode
  const handleValueChange = useCallback(
    (newValue: string) => {
      if (isFormikMode && props.formik && props.fieldPrefix) {
        props.formik.setFieldValue(props.fieldPrefix, newValue);
        props.formik.setFieldTouched(props.fieldPrefix, true);
      } else if ("onChange" in props && props.onChange) {
        props.onChange(newValue);
      }
    },
    [isFormikMode, props],
  );

  // Utiliser les patterns de syntaxe communs
  const syntaxPatterns = useMemo(
    () => getSyntaxPatterns(silaeVariables),
    [silaeVariables],
  );

  // Variables pour le sélecteur de variables
  const frequentlyUsedVariables = useMemo(
    () => getFrequentlyUsedVariablesData,
    [getFrequentlyUsedVariablesData],
  );
  const filteredVariables = useMemo(() => {
    return silaeVariables.filter(
      (variable) =>
        variable.code
          .toLowerCase()
          .includes(variableSearchTerm.toLowerCase()) ||
        variable.definition
          .toLowerCase()
          .includes(variableSearchTerm.toLowerCase()),
    );
  }, [variableSearchTerm, silaeVariables]);

  // Fonction pour calculer la position optimale de la liste d'auto-complétion
  const calculateCompletionPosition = useCallback(
    (cursorPosition: number) => {
      const textarea = textareaRef.current;
      if (!textarea) return { top: 8, left: 8 };

      // Calculer la position du curseur dans le textarea
      const textBeforeCursor = value.substring(0, cursorPosition);
      const lines = textBeforeCursor.split("\n");
      const currentLine = lines.length - 1;
      const currentColumn = lines[lines.length - 1].length;

      // Obtenir les dimensions du conteneur
      const containerRect = textarea.parentElement?.getBoundingClientRect();

      if (!containerRect) return { top: 8, left: 8 };

      // Calculer la position approximative du curseur
      const lineHeight = 24; // Hauteur approximative d'une ligne
      const charWidth = 8; // Largeur approximative d'un caractère

      const cursorTop = currentLine * lineHeight + 12; // 12px de padding
      const cursorLeft = currentColumn * charWidth + 12; // 12px de padding

      // Dimensions de la liste d'auto-complétion
      const completionWidth = 4 * 80; // min-w-80 max-w-96 = 320-384px
      const completionHeight = 4 * 60; // max-h-60 = 240px

      // Calculer la position optimale
      let top = cursorTop + 20; // 20px sous le curseur
      let left = cursorLeft;

      // Ajuster si la liste dépasse à droite
      if (left + completionWidth > containerRect.width) {
        left = Math.max(8, containerRect.width - completionWidth - 8);
      }

      // Ajuster si la liste dépasse en bas
      if (top + completionHeight > containerRect.height) {
        top = Math.max(8, cursorTop - completionHeight - 20);
      }

      return { top, left };
    },
    [value],
  );

  // Fonction pour appliquer la coloration syntaxique
  const applySyntaxHighlightingCallback = useCallback(
    (text: string): React.ReactElement[] => {
      const elements = applySyntaxHighlighting(
        text,
        syntaxColors,
        syntaxPatterns,
      );
      return elements.map((element) => (
        <span key={element.key} className={element.className}>
          {element.text}
        </span>
      ));
    },
    [syntaxPatterns],
  );

  // Fonction pour insérer une suggestion
  const insertCompletion = (
    completion: CompletionItem,
    wordStart: number,
    wordEnd: number,
    isCallContext: boolean = false,
  ) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    let insertText = completion.insertText || completion.label;

    // Si on est dans un contexte "call", on ne veut pas dupliquer "call"
    if (isCallContext) {
      const textBefore = value.substring(0, wordStart);
      if (textBefore.toLowerCase().endsWith("call ")) {
        const functionName = completion.label.replace(/^call\s+/i, "").trim();
        insertText = functionName;
      }
    }

    const newValue =
      value.substring(0, wordStart) + insertText + value.substring(wordEnd);

    handleValueChange(newValue);
    setShowCompletions(false);

    // Positionner le curseur après l'insertion
    setTimeout(() => {
      const newCursorPos = wordStart + insertText.length;
      textarea.selectionStart = textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);
  };

  // Mettre à jour la coloration quand le contenu change
  useEffect(() => {
    setHighlightedContent(applySyntaxHighlightingCallback(value));
  }, [value, applySyntaxHighlightingCallback]);

  // Fonction pour synchroniser le scroll entre textarea et zone de coloration
  const handleScroll = useCallback(() => {
    const textarea = textareaRef.current;
    const highlight = highlightRef.current;
    if (textarea && highlight) {
      highlight.scrollTop = textarea.scrollTop;
      highlight.scrollLeft = textarea.scrollLeft;
    }
  }, []);

  // Fonction pour insérer une variable SILAE à la position du curseur
  const insertVariable = useCallback(
    (variable: SilaeVariable) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const cursorPosition = textarea.selectionStart;
      const newValue =
        value.substring(0, cursorPosition) +
        variable.code +
        value.substring(cursorPosition);

      handleValueChange(newValue);

      // Positionner le curseur après la variable insérée
      setTimeout(() => {
        const newCursorPosition = cursorPosition + variable.code.length;
        textarea.selectionStart = textarea.selectionEnd = newCursorPosition;
        textarea.focus();
      }, 0);

      setVariableSelectorOpen(false);
      setSelectedVariable(null);
      setVariableSearchTerm("");
    },
    [value, handleValueChange],
  );

  // Gérer les changements de contenu
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value.replace(/[\r\n]/g, ""); // Supprimer les retours à la ligne
    const textarea = e.target;
    const cursorPosition = textarea.selectionStart;

    // Vérifier si c'est une insertion de parenthèse
    const oldValue = value;
    if (newValue.length > oldValue.length) {
      const insertedChar = newValue[cursorPosition - 1];
      const bracketChars = ["(", "[", "{", '"', "'"];

      if (bracketChars.includes(insertedChar)) {
        const { newValue: bracketValue, newCursorPosition } =
          handleBracketInsertion(oldValue, cursorPosition - 1, insertedChar);

        // Mettre à jour la valeur avec l'auto-complétion des parenthèses
        handleValueChange(bracketValue);

        // Positionner le curseur au bon endroit
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = newCursorPosition;
        }, 0);

        return;
      }
    }

    handleValueChange(newValue);

    // Gérer l'auto-complétion
    const { word, isVariableContext, isCallContext, prefix } = getCurrentWord(
      newValue,
      cursorPosition,
    );

    // Déclencher l'auto-complétion si on a un mot OU si on est dans un contexte "call"
    if (word.length >= 1 || isCallContext) {
      const filteredCompletions = getFilteredCompletions(
        word,
        isVariableContext,
        isCallContext,
        prefix,
        silaeVariables,
      );
      if (filteredCompletions.length > 0) {
        setCompletions(filteredCompletions);
        setSelectedCompletionIndex(0);
        setCompletionPosition(calculateCompletionPosition(cursorPosition));
        setShowCompletions(true);
      } else {
        setShowCompletions(false);
      }
    } else {
      setShowCompletions(false);
    }
  };

  // Gérer les touches et la navigation dans les suggestions
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Gérer l'auto-complétion
    if (showCompletions && completions.length > 0) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedCompletionIndex((prev) =>
            prev < completions.length - 1 ? prev + 1 : 0,
          );
          // Mettre à jour la position si nécessaire
          setCompletionPosition(
            calculateCompletionPosition(textarea.selectionStart),
          );
          return;

        case "ArrowUp":
          e.preventDefault();
          setSelectedCompletionIndex((prev) =>
            prev > 0 ? prev - 1 : completions.length - 1,
          );
          // Mettre à jour la position si nécessaire
          setCompletionPosition(
            calculateCompletionPosition(textarea.selectionStart),
          );
          return;

        case "Enter":
        case "Tab": {
          e.preventDefault();
          const selectedCompletion = completions[selectedCompletionIndex];
          const cursorPosition = textarea.selectionStart;
          const { start, end, isCallContext } = getCurrentWord(
            value,
            cursorPosition,
          );
          insertCompletion(selectedCompletion, start, end, isCallContext);
          return;
        }

        case "Escape":
          e.preventDefault();
          setShowCompletions(false);
          return;
      }
    } else if (e.key === "Enter") {
      // Empêcher les retours à la ligne quand l'auto-complétion n'est pas active
      e.preventDefault();
      return;
    }
  };

  const getVariableIcon = (type: SilaeVariableType) => {
    switch (type) {
      case "function":
        return <FunctionSquare className="h-4 w-4" />;
      case "variable":
        return <Variable className="h-4 w-4" />;
      default:
        return <Variable className="h-4 w-4" />;
    }
  };

  return (
    <>
      <div
        className={cn(
          "relative flex flex-col bg-background border border-border rounded-lg",
          error && "border-red-500",
          className,
        )}
        style={{ height }}
      >
        {/* Barre d'outils avec sélecteur de variables intégré */}
        <div className="px-3 py-2 bg-muted border-b border-border flex justify-between items-center">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">
              {t("silaeExpressionEditor.title", "Expression SILAE")}
            </span>
          </div>
          <VariableSelector
            selectedVariable={selectedVariable}
            onVariableSelect={insertVariable}
            searchTerm={variableSearchTerm}
            setSearchTerm={setVariableSearchTerm}
            open={variableSelectorOpen}
            setOpen={setVariableSelectorOpen}
            frequentlyUsedVariables={frequentlyUsedVariables}
            filteredVariables={filteredVariables}
            hasLabel={false}
            className=""
            placeholder={t("silaeExpressionEditor.selectVariable")}
          />
        </div>

        {/* Zone d'édition principale */}
        <div className="relative flex-1 overflow-y-auto">
          {/* Zone de coloration syntaxique (arrière-plan) */}
          <div
            ref={highlightRef}
            className="absolute inset-0 p-3 font-mono text-sm whitespace-pre-wrap break-words pointer-events-none text-foreground"
            style={{
              fontFamily:
                "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
              lineHeight: "1.5",
            }}
          >
            {highlightedContent}
          </div>

          {/* Zone de saisie (transparente par-dessus) */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onScroll={handleScroll}
            placeholder={placeholder || t("silaeExpressionEditor.placeholder")}
            readOnly={readOnly}
            className={cn(
              "absolute inset-0 w-full h-full p-3 font-mono text-sm bg-transparent text-transparent caret-foreground resize-none outline-none overflow-auto",
              readOnly && "cursor-not-allowed opacity-50",
            )}
            style={{
              fontFamily:
                "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
              lineHeight: "1.5",
            }}
            spellCheck={false}
          />

          {/* Popup d'auto-complétion */}
          {showCompletions && completions.length > 0 && (
            <div
              className="absolute z-50 bg-popover border border-border rounded-lg shadow-2xl max-h-60 overflow-y-auto min-w-80 max-w-96 backdrop-blur-sm"
              style={{
                top: `${completionPosition.top}px`,
                left: `${completionPosition.left + 15}px`,
              }}
            >
              <div className="p-2 border-b border-border">
                <div className="text-xs text-muted-foreground font-medium">
                  {t("silaeExpressionEditor.suggestions.title", {
                    count: completions.length,
                  })}
                </div>
              </div>
              {completions.map((completion, index) => (
                <div
                  key={completion.label}
                  className={cn(
                    "px-3 py-2 cursor-pointer border-b border-border last:border-b-0 transition-colors",
                    {
                      "bg-accent": index === selectedCompletionIndex,
                      "hover:bg-muted": index !== selectedCompletionIndex,
                    },
                  )}
                  onClick={() => {
                    const textarea = textareaRef.current;
                    if (textarea) {
                      const cursorPosition = textarea.selectionStart;
                      const { start, end, isCallContext } = getCurrentWord(
                        value,
                        cursorPosition,
                      );
                      insertCompletion(completion, start, end, isCallContext);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {getVariableIcon(completion.type as SilaeVariableType)}
                    </span>
                    <div>
                      <span className="font-mono text-sm text-foreground">
                        {completion.label}
                      </span>
                      {completion.description && (
                        <div className="text-xs text-muted-foreground">
                          {completion.description}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="p-2 border-t border-border">
                <div className="text-xs text-muted-foreground">
                  {t("silaeExpressionEditor.suggestions.instructions")}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {error && <span className="text-sm text-red-500 mt-1">{error}</span>}
    </>
  );
};
